import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import MoleculeViewer from "../components/MoleculeViewer";
import { Sparkles, Search } from "lucide-react";

const SAMPLES = ["H2O", "CH3OH", "CH4", "CO2", "NH3", "C2H4", "H2SO4", "C6H6"];

export default function MoleculeSearch() {
  const [formula, setFormula] = useState("");
  const [mol, setMol] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const runLookup = useCallback(async (raw) => {
    const q = raw.trim();
    if (!q) return;
    setFormula(raw);
    setBusy(true);
    setErr(null);
    try {
      const { data } = await api.get(`/molecules/lookup`, { params: { formula: q } });
      setMol(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not build that molecule.");
      setMol(null);
    } finally {
      setBusy(false);
    }
  }, []);
  const lookup = (f) => runLookup(f ?? formula);

  useEffect(() => { runLookup("H2O"); }, [runLookup]);

  return (
    <div className="space-y-8" data-testid="molecule-search-root">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1 text-xs uppercase tracking-widest text-cyan-300">
          <Sparkles size={12} /> Molecule Lab
        </div>
        <h1 className="font-display mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Type a formula. See it in 3D.</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-400">
          Try water, methanol, benzene — anything. If we don't know the exact geometry, we generate a fair approximation.
        </p>
      </div>

      <div className="rounded-md border border-white/10 bg-black/40 p-6">
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            value={formula}
            onChange={(e) => setFormula(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && lookup()}
            placeholder="e.g. CH3OH"
            data-testid="molecule-input"
            className="font-mono-num flex-1 rounded-md border border-white/10 bg-black/60 px-4 py-3 text-lg text-slate-100 placeholder:text-slate-500 focus:border-cyan-400/60 focus:outline-none"
          />
          <button
            onClick={() => lookup()}
            disabled={busy}
            data-testid="molecule-search-btn"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-cyan-500 px-6 py-3 text-sm font-semibold text-black hover:bg-cyan-400 disabled:opacity-50"
          >
            <Search size={14} /> Build
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {SAMPLES.map((s) => (
            <button
              key={s}
              onClick={() => lookup(s)}
              data-testid={`molecule-sample-${s}`}
              className="font-mono-num rounded-md border border-white/10 bg-white/5 px-3 py-1 text-xs text-slate-300 hover:border-cyan-400/40 hover:text-cyan-200"
            >
              {s}
            </button>
          ))}
        </div>
        {err && <div className="mt-3 rounded-md border border-red-400/40 bg-red-500/10 p-3 text-xs text-red-200">{err}</div>}
      </div>

      {mol && (
        <div>
          <MoleculeViewer molecule={mol} />
          {mol.generated && (
            <p className="mt-3 text-xs text-slate-500">
              Approximated layout — bond geometry may not match real chemistry.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
